import React, { useState, useEffect, useRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from '@emailjs/browser';
import { format } from 'date-fns';

export function BookingRequest() {
    const [inputFields, setInputFields] = useState({
        name: "",
        email: "",
        phoneNumber: "",
        bookingDate: "",
        bookingTime: "",
        bookingTotalHours: "",
        location: "",
        subject: "",
        message: ""
    });
    const[errors, setErrors] = useState({});
    const[showNameError, setShowNameError] = useState(false);
    const[showEmailError, setShowEmailError] = useState(false);
    const[showPhoneNumberError, setShowPhoneNumberError] = useState(false);
    const[showBookingDateError, setShowBookingDateError] = useState(false);
    const[showBookingTimeError, setShowBookingTimeError] = useState(false);
    const[showBookingTotalHoursError, setShowBookingTotalHoursErrors] = useState(false);
    const[showLocationError, setShowLocationError] = useState(false);
    const[showMessageError, setShowMessageError] = useState(false);
    const[showReCaptchaError, setShowReCaptchaError] = useState(false);
    const[submitting, setSubmitting] = useState(false);

    const inputRefName = useRef(null);
    const inputRefEmail = useRef(null);
    const inputRefPhoneNumber = useRef(null);
    const inputRefBookingDate = useRef(null);
    const inputRefBookingTime = useRef(null);
    const inputRefBookingTotalHours = useRef(null);
    const inputRefLocation = useRef(null);
    const inputRefMessage = useRef(null);
    const form = useRef();
    const [showContactFormSubmitError, setShowContactFormSubmitError] = useState(false);
    const [reCaptchaChecked, setReCaptchaChecked] = useState(false);

    //reCaptchaKey
    const reCaptchaKey = '6Lcja6kqAAAAAFuQizHFq3k3_sDqak21TR7yodhw'; //V2 Version

    //emailJS IDs
    const serviceID = 'default_service';
    const templateID = 'template_83j137p';
    const publicKey = 'SPgavL7CoXg4T4WHy';

    const currentDate = new Date();

    const validateEmail = (email) => {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

        return emailRegex.test(email);
    };

    const validateValues = (inputValues) => {
        let errors = {};

        if (inputValues.name.length === 0) {
            errors.name = "You must enter your name.";
            setShowNameError(true);
        }

        if ((inputValues.email.length === 0) || (!validateEmail(inputValues.email))) {
            errors.email = "You must enter a valid email address.";
            setShowEmailError(true);
        }

        if (inputValues.phoneNumber.length === 0) {
            errors.phoneNumber = "You must enter your phone number.";
            setShowPhoneNumberError(true);
        }

        if (inputValues.bookingDate.length === 0) {
            errors.bookingDate = "You must select a booking date.";
            setShowBookingDateError(true);
        }

        if (inputValues.bookingTime.length === 0) {
            errors.bookingTime = "You must select a booking time.";
            setShowBookingTimeError(true);
        }

        if (inputValues.bookingTotalHours.length === 0) {
            errors.bookingTotalHours = "You must enter total booking hours.";
            setShowBookingTotalHoursErrors(true);
        }

        if (inputValues.location.length === 0) {
            errors.location = "You must enter your booking location.";
            setShowLocationError(true);
        }

        if (inputValues.message.length === 0) {
            errors.message = "You must enter your message.";
            setShowMessageError(true);
        }


        return errors;
    };

    const handleChange = (e) => {
        setInputFields({
            ...inputFields, [e.target.name]: e.target.value
        });

        if (e.target.name === "name") {
            if (e.target.value === "") {
                setShowNameError(true);
                errors.name = "You must enter your name.";
            } else {
                setShowNameError(false);
            }
        }

        if (e.target.name === "email") {
            if ((e.target.value === "") || (!validateEmail(e.target.value))) {
                setShowEmailError(true);
                errors.email = "You must enter a valid email address.";
            } else {
                setShowEmailError(false);
            }
        }

        if (e.target.name === "phoneNumber") {
            if (e.target.value === "") {
                setShowPhoneNumberError(true);
                errors.phoneNumber = "You must enter your phone number.";
            } else {
                setShowPhoneNumberError(false);
            }
        }

        if (e.target.name === "bookingDate") {
            if (e.target.value === "") {
                setShowBookingDateError(true);
                errors.bookingDate = "You must select a booking date.";
            } else {
                setShowBookingDateError(false);
            }
        }

        if (e.target.name === "bookingTime") {
            if (e.target.value === "") {
                setShowBookingTimeError(true);
                errors.bookingTime = "You must enter a booking time.";
            } else {
                setShowBookingTimeError(false);
            }
        }

        if (e.target.name === "bookingTotalHours") {
            if (e.target.value === "") {
                setShowBookingTotalHoursErrors(true);
                errors.bookingTotalHours = "You must enter total booking hours.";
            } else {
                setShowBookingTotalHoursErrors(false);
            }
        }

        if (e.target.name === "location") {
            if (e.target.value === "") {
                setShowLocationError(true);
                errors.location = "You must enter your booking location.";
            } else {
                setShowLocationError(false);
            }
        }

        if (e.target.name === "message") {
            if (e.target.value === "") {
                setShowMessageError(true);
                errors.message = "You must enter your message.";
            } else {
                setShowMessageError(false);
            }
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setErrors(validateValues(inputFields));
        setSubmitting(true);
    };

    const handleReset = useCallback(() => {
        inputRefName.current.value = "";
        inputRefEmail.current.value = "";
        inputRefPhoneNumber.current.value = "";
        inputRefBookingDate.current.value = "";
        inputRefBookingTime.current.value = "";
        inputRefBookingTotalHours.current.value = "";
        inputRefLocation.current.value = "";
        inputRefMessage.current.value = "";

        setInputFields({
            name: "",
            email: "",
            phoneNumber: "",
            bookingDate: "",
            bookingTime: "",
            bookingTotalHours: "",
            location: "",
            subject: "",
            message: ""
        });
    }, []);

    const sendEmail = useCallback(() => {
        emailjs.init(publicKey);
        emailjs.sendForm(serviceID, templateID, '#form').then(() => {
            handleReset();
            setShowContactFormSubmitError(false);
        }, (err) => {
            console.log(JSON.stringify(err));
            setSubmitting(false);
            setShowContactFormSubmitError(true);
        });
    }, [publicKey, serviceID, templateID, handleReset]);

    const finishSubmit = useCallback(() => {
        if (reCaptchaChecked) {
            sendEmail();
        } else {
            setSubmitting(false);
            setShowReCaptchaError(true);
            setErrors((prev) => ({ ...prev, reCatpcha: "Validate if you are human." }));
        }
    }, [reCaptchaChecked, sendEmail]);

    const captchaOnChange = (e) => {
        setReCaptchaChecked(true);
        setShowReCaptchaError(false);
    };

    useEffect(() => {
        if (Object.keys(errors).length === 0 && submitting) {
            finishSubmit();
        }
    }, [errors, submitting, finishSubmit]);


    return (
        <div className="container">
            <h2 className="heading-secondary">Bookings</h2>
            <p>Fill out the form below to reach Zayne's management team for booking a show.</p>
            <p>All fields required.</p>
            
                        <div className="row">
                            <div className="col-lg-8 col-md-12 col-sm-12 col-xs-12 no-padding">
                            {Object.keys(errors).length === 0 && submitting ? (
                                <span className="banner success">Your booking request has been successfully sent! Zayne's management team will contact you within 1-2 business days.</span>
                            ) : null}

                            {showContactFormSubmitError ? (
                                <span className="banner error">We are currently experiencing technical issues with our contact form and are unable to send your message. Please try again later.</span>
                            ) : null}

                                <form ref={form} id="form" onSubmit={handleSubmit} noValidate>
                                    {/* Name */}
                                    <div className="field">
                                        <label htmlFor="name">Name</label>
                                        <input type="text" placeholder="Enter your name" name="name" id="name" value={inputFields.name} onChange={handleChange} className={ showNameError ? "input-error" : "" } ref={ inputRefName } required />
                                        {showNameError ? (
                                            <span className="input-error-message spacer" id="inputErrorMessageName"><i className="fa-solid fa-triangle-exclamation"></i> {errors.name}</span>
                                        ) : null}                                  
                                    </div>

                                    {/* Email */}
                                    <div className="field">
                                        <label htmlFor="email">Email</label>
                                        <input type="email" placeholder="Enter your email address" name="email" id="email" value={inputFields.email} onChange={handleChange} className={ showEmailError ? "input-error" : "" } ref={ inputRefEmail } required />
                                        {showEmailError ? (
                                            <span className="input-error-message spacer" id="inputErrorMessageEmail"><i className="fa-solid fa-triangle-exclamation"></i> {errors.email}</span>
                                        ) : null} 
                                    </div>

                                    {/* Phone Number */}
                                    <div className="field">
                                        <label htmlFor="phoneNumber">Phone number</label>
                                        <input type="text" placeholder="Enter your phone number, e.g 1-214-456-7890" name="phoneNumber" id="phoneNumber" value={inputFields.phoneNumber} onChange={handleChange} className={ showPhoneNumberError ? "input-error" : "" } ref={ inputRefPhoneNumber } required />
                                        {showPhoneNumberError ? (
                                            <span className="input-error-message spacer" id="inputErrorMessagePhoneNumber"><i className="fa-solid fa-triangle-exclamation"></i> {errors.phoneNumber}</span>
                                        ) : null}                                  
                                    </div>
                                    
                                    {/* Booking Date and Time */}
                                    <div className="row">
                                        <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12 no-padding">
                                            <div className="field">
                                                <label htmlFor="bookingDate">Booking Date</label>
                                                <input type="date" min={ format(currentDate, 'yyyy-MM-dd') } placeholder="Select booking date" name="bookingDate" id="bookingDate" value={inputFields.bookingDate} onChange={handleChange} className={ showBookingDateError ? "input-error" : "" } ref={ inputRefBookingDate } required />
                                                {showBookingDateError ? (
                                                    <span className="input-error-message spacer" id="inputErrorMessageBookingDate"><i className="fa-solid fa-triangle-exclamation"></i> {errors.bookingDate}</span>
                                                ) : null}                                  
                                            </div>
                                        </div>
                                        <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12 field-padding">
                                            <div className="field">
                                                <label htmlFor="bookingTime">Booking Time</label>
                                                <input type="time" placeholder="Select booking time" name="bookingTime" id="bookingTime" value={inputFields.bookingTime} onChange={handleChange} className={ showBookingTimeError ? "input-error" : "" } ref={ inputRefBookingTime } required />
                                                {showBookingTimeError ? (
                                                    <span className="input-error-message spacer" id="inputErrorMessageBookingTime"><i className="fa-solid fa-triangle-exclamation"></i> {errors.bookingTime}</span>
                                                ) : null}                                  
                                            </div>
                                        </div>
                                    </div>

                                    {/* Booking Total Hours */}
                                    <div className="field">
                                        <label htmlFor="bookingTotalHours">Total Booking Hours</label>
                                        <input type="text" placeholder="Enter how many hours you need booked" name="bookingTotalHours" id="bookingTotalHours" value={inputFields.bookingTotalHours} onChange={handleChange} className={ showBookingTotalHoursError ? "input-error" : "" } ref={ inputRefBookingTotalHours } required />
                                        {showBookingTotalHoursError ? (
                                            <span className="input-error-message spacer" id="inputErrorMessageBookingTotalHours"><i className="fa-solid fa-triangle-exclamation"></i> {errors.bookingTotalHours}</span>
                                        ) : null}                                  
                                    </div>
                                    
                                    {/* Location */}
                                    <div className="field">
                                        <label htmlFor="location">Location</label>
                                        <input type="text" placeholder="Enter venue name, location and address" name="location" id="location" value={inputFields.location} onChange={handleChange} className={ showLocationError ? "input-error" : "" } ref={ inputRefLocation } required />
                                        {showLocationError ? (
                                            <span className="input-error-message spacer" id="inputErrorMessageLocation"><i className="fa-solid fa-triangle-exclamation"></i> {errors.location}</span>
                                        ) : null}                                  
                                    </div>

                                    {/* Message */}
                                    <div className="field">
                                        <label htmlFor="message">Message</label>
                                        <textarea id="message" name="message" rows="4" cols="50" placeholder="Provide us with any other details about your booking request" value={inputFields.message} onChange={handleChange} className={ showMessageError ? "input-error" : "" } ref={ inputRefMessage } required></textarea>
                                        {showMessageError ? (
                                            <span className="input-error-message" id="inputErrorMessageMessage"><i className="fa-solid fa-triangle-exclamation"></i> {errors.message}</span>
                                        ) : null} 
                                    </div>

                                    {/* ReCAPTCHA */}
                                    <div className="field">
                                        <ReCAPTCHA sitekey={reCaptchaKey} onChange={captchaOnChange} />
                                        {showReCaptchaError ? (
                                            <span className="input-error-message" id="inputErrorReCaptcha"><i className="fa-solid fa-triangle-exclamation"></i> {errors.reCatpcha}</span>
                                        ) : null}
                                    </div>
                                    <div className="field actions">
                                        <button className="primary-button g-recaptcha" type="submit" value="Send booking request">Send booking request</button>
                                    </div>                  
                                </form>
                            </div>
                        </div>
        </div>
    );
}